/**
 * Leaf module for `EventTypeDefinitionSchema` + `EventTypeDefinition`.
 *
 * Extracted from `config-boundary.ts` to break the cycle:
 *   config-boundary.ts → event-colors.ts → event-type-seed.ts → config-boundary.ts
 *
 * Import this module directly when working with event-type config primitives.
 */

import { z } from "zod";

/**
 * Event type definition schema — denormalised cache of the per-org
 * `EventType` table emitted on every AppConfig response. The new
 * authoritative shape is the `EventType` Prisma row (see
 * `contracts/event-types.contract.ts`); this list lets calendar runtime
 * code paths (reflow, legend, capacity enforcement, color matcher) look up
 * per-type config without a second round-trip.
 *
 * Replaces all of:
 *   - `eventDurationSettings.perTypeMinutes[id]`        → `durationMinutes`
 *   - `slotCapacitySettings.agent.perTypeMax[id]`       → `maxPerSlotAgent`
 *   - `slotCapacitySettings.secretary.perTypeMax[id]`   → `maxPerSlotSecretary`
 *   - `eventCapacitySettings.allowMultipleFutureEvents` → `allowMultipleFutureEvents`
 *   - `eventCapacitySettings.allowMultipleEventsPerDay` → `allowMultipleEventsPerDay`
 *   - `eventCapacitySettings.maxFutureEventsPerType[id]`→ `maxFutureEventsPerType`
 */
export const EventTypeDefinitionSchema = z.object({
	id: z.string().min(1),
	name: z.string().min(1),
	durationMinutes: z
		.number()
		.int()
		.min(1)
		.max(60 * 24),
	maxPerSlotAgent: z.number().int().min(1).nullable(),
	maxPerSlotSecretary: z.number().int().min(1).nullable(),
	allowMultipleFutureEvents: z.boolean(),
	allowMultipleEventsPerDay: z.boolean(),
	maxFutureEventsPerType: z.number().int().min(1).max(20).nullable(),
	// Per-role booking horizon: max days ahead of "today" (org timezone) this
	// type may be booked. `null` = unlimited for that role.
	bookingHorizonAgentDays: z.number().int().min(1).nullable(),
	bookingHorizonSecretaryDays: z.number().int().min(1).nullable(),
	minimumBookingNotice: z.number().int().min(0),
	beforeBufferMinutes: z.number().int().min(0),
	afterBufferMinutes: z.number().int().min(0),
	requiresApproval: z.boolean(),
	approvalTtlHours: z.number().int().min(1).nullable(),
	// Owner-authored free text (price, capacity, what's included, ...).
	// Surfaced to the AI agent so it can relay these details to guests; also
	// shown in the config drawer and the scheduling UI.
	description: z.string().max(2000).nullable(),
	// Multi-day spans: max org-local calendar NIGHTS one booking of this type may
	// cover. `null` = fixed duration (ends at eventDateTime + durationMinutes).
	maxSpanDays: z.number().int().min(1).max(732).nullable(),
	// Resource this type belongs to (chalet, room, chair, practitioner), by
	// name; `null` = not resource-scoped. The resource list is the distinct
	// values, and each one's settings live in `EventResource` under that name.
	resourceName: z.string().trim().min(1).max(60).nullable(),
	// Most bookings that resource holds at one instant, across every type on it
	// (`EventResource.maxOccupancy`); `null`/absent = no limit. Bookings of
	// different types collide only through a shared resource, and only past this.
	resourceMaxOccupancy: z.number().int().min(1).nullish(),
});
export type EventTypeDefinition = z.infer<typeof EventTypeDefinitionSchema>;
